import { AuxiliumComponent } from "../auxilium-component";
import { Browser } from "../../core/browser";

export default class Grid extends AuxiliumComponent {
	static observable = ["gap", "alignment", "columns", ...Browser.Breakpoints.map(({ size }) => `columns-${size}`)];

	/**
	 * How much of the requested column count to keep at each screen size when nothing has been
	 * asked for explicitly, so a grid sheds columns as the screen narrows instead of squeezing
	 * all of them onto a phone. Sizes left out here keep the count they were given.
	 */
	static #collapse = {
		xs: () => 1,
		sm: count => Math.ceil(count / 3),
		md: count => Math.ceil(count * 2 / 3)
	};

	#placementStyles = null;

	get #styles() {
		this.#placementStyles ??= document.createElement("style");

		if (this.#placementStyles.parentElement !== this) { this.appendChild(this.#placementStyles); }

		return this.#placementStyles;
	}

	get #list() {
		return this.querySelector(":scope > ul");
	}

	/**
	 * Works out how many columns the grid has at each screen size. A count given for a size holds
	 * from that size upwards, and the sizes nothing has been given for scale the `columns` count
	 * back so the grid still fits a narrow screen.
	 */
	#resolveColumns() {
		const requested = parseInt(this.getAttribute("columns")) || 0,
			columns = {};

		let resolved = 0;

		for (const { size } of Browser.Breakpoints) {
			const given = parseInt(this.getAttribute(`columns-${size}`)) || 0;

			if (given) { resolved = given; }
			else if (requested) { resolved = Math.max(1, (Grid.#collapse[size] ?? (count => count))(requested)); }

			columns[size] = resolved || 1;
		}

		return columns;
	}

	/**
	 * Works out what an item covers at each screen size, never letting it reach past the columns
	 * that size actually has. Left alone, a span wider than the grid builds columns of its own and
	 * carries the whole layout off the side of the screen.
	 */
	#resolveSpans(item, columns) {
		const spans = {},
			given = (attribute) => parseInt(item?.getAttribute(attribute)) || 0;

		let column = given("column-span") || 1,
			row = given("row-span") || 1;

		for (const { size } of Browser.Breakpoints) {
			column = given(`column-span-${size}`) || column;
			row = given(`row-span-${size}`) || row;

			spans[size] = {
				column: Math.min(column, columns[size]),
				// Covering rows only holds an item beside its neighbours, which a single column hasn't got.
				row: columns[size] > 1 ? row : 1
			};
		}

		return spans;
	}

	#buildRules(columns, spans) {
		// Seeded with what the stylesheet already says, so a grid that wants nothing else says nothing.
		const selector = `#${CSS.escape(this.id)}`,
			applied = { columns: 1, spans: spans.map(() => ({ column: 1, row: 1 })) };

		return Browser.Breakpoints.map(({ size, width }) => {
			const rules = [];

			if (columns[size] !== applied.columns) {
				applied.columns = columns[size];
				rules.push(`${selector}>ul{--grid-columns:${columns[size]}}`);
			}

			spans.forEach((span, index) => {
				const declarations = ["column", "row"]
					.filter(axis => span[size][axis] !== applied.spans[index][axis])
					.map(axis => `--${axis}-span:${span[size][axis]}`);

				if (declarations.length) {
					applied.spans[index] = span[size];
					rules.push(`${selector}>ul>li:nth-child(${index + 1}){${declarations.join(";")}}`);
				}
			});

			// Each size states only what the size below it hasn't, so the rules stack up as the screen widens.
			return rules.length && width ? `@media (min-width:${width}px){${rules.join("")}}` : rules.join("");
		}).join("");
	}

	/**
	 * Rewrites the column count and every item's placement for each screen size. Spans have to
	 * reach the stylesheet as plain numbers, since browsers turn down a span worked out with
	 * calc() or min(), so a size at a time is written out here instead.
	 */
	refreshPlacement() {
		this.id ||= `gr${crypto.randomUUID().slice(0, 8)}`;

		const columns = this.#resolveColumns(),
			spans = [...(this.#list?.children ?? [])].map(item => this.#resolveSpans(item.firstElementChild, columns));

		this.#styles.textContent = this.#buildRules(columns, spans);
	}

	parsedCallback() {
		const list = document.createElement("ul");

		[...this.childNodes ?? []]
			.filter(x => x !== this.#placementStyles && (x.nodeType !== Node.TEXT_NODE || x.textContent.trim()))
			.forEach(item => this.#addItem(item, list));

		this.appendChild(list);
		this.refreshPlacement();
	}

	#addItem(item, list) {
		list ??= this.#list ?? this.appendChild(document.createElement("ul"));

		list.appendChild(document.createElementWithContents("li", item));
	}

	addItem(item, list) {
		this.#addItem(item, list);
		this.refreshPlacement();
	}

	addItems(items) {
		items.forEach(item => this.#addItem(item));
		this.refreshPlacement();
	}

	attributeChanged(name) {
		if (name.startsWith("columns")) {
			this.refreshPlacement();
		}
	}
}
